import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Select, MenuItem, CircularProgress as MuiCircularProgress,
} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import useSWR from 'swr';
import { dataFetcher, endpoints } from 'Api';
import { logAppEvent } from 'Api/errorLogger';
import { AppContext } from 'Context';
import { Pathname } from 'Routes';
import { Page } from 'Components/page';
import PermissionDeniedModal from 'Components/PermissionDeniedModal';
import {
  GridShimmer, ViewerGrid, ViewerThemeProvider, ViewerAurora,
} from 'Components/viewer-inner';
import { useProjectorBg } from 'hooks/useProfileBg';

const CircularProgress = withStyles({ circle: { color: '#fff !important' } })(MuiCircularProgress);

// Maps the dark sort dropdown to the getCategoryDetail sort contract
// (sort_by: layout | name | date, sort_order: ASC | DESC).
const SORT_OPTIONS = [
  { value: 'layout:DESC', label: 'Default', by: 'layout', order: 'DESC' },
  { value: 'name:ASC', label: 'Name A–Z', by: 'name', order: 'ASC' },
  { value: 'name:DESC', label: 'Name Z–A', by: 'name', order: 'DESC' },
  { value: 'date:DESC', label: 'Newest', by: 'date', order: 'DESC' },
  { value: 'date:ASC', label: 'Oldest', by: 'date', order: 'ASC' },
];

const DARK_SELECT_SX = {
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  bgcolor: 'rgba(255,255,255,0.06)',
  borderRadius: 999,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.14)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiSelect-select': { py: 1, pl: 2 },
};

export const InnerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isFromGift = searchParams.get('fromGift') === 'true';
  const {
    setAppSnackbar, userDetails, subscriptionDetails,
  } = useContext(AppContext);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategoryID, setSelectedCategoryID] = useState(undefined);
  const [profileID, setProfileID] = useState(undefined);
  const projectorBg = useProjectorBg(profileID);
  const [title, setTitle] = useState(undefined);
  const [thumbnail, setThumbnail] = useState(undefined);
  const [loader, setLoader] = useState(true);
  const [start, setStart] = useState(0);
  const [limit] = useState(15);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Synchronous guard for the scroll handler — `isLoadingMore` state only
  // updates on the next render, so several native scroll events firing in the
  // same tick can all read the stale `false` and each queue a `setStart`,
  // silently skipping a whole page (start jumps 15 → 45, losing 15-30).
  const isLoadingMoreRef = useRef(false);
  const [isSorting, setIsSorting] = useState(false);
  const [sortBy, setSortBy] = useState('layout');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  // Ordered category list + position (from the profile tiles) so we can offer a
  // "next category" jump. Switching is in place — no remount.
  const [categories] = useState(location?.state?.categories || []);
  const [catIndex, setCatIndex] = useState(location?.state?.index ?? 0);
  // Check if current plan is 14 for sort parameters
  const currentPlanId = subscriptionDetails?.subscription?.plan_id
    || subscriptionDetails?.plans?.[subscriptionDetails?.plans?.length - 1]?.plan_id;
  const isPlan14 = String(currentPlanId) === '14';

  // The app's global SWRConfig (src/index.jsx) defaults revalidateOnMount to
  // false — that's an app-wide "don't auto-refetch" policy, and it applies to
  // key CHANGES too, not just mount. So this hook never fetches on its own;
  // every fetch (first load and every load-more/sort-change) has to be
  // requested explicitly via the bound `mutate()` below.
  const { mutate: getCategoryDetails } = useSWR(
    // Gate the key on both ids being ready — otherwise a manual trigger could
    // fire a fetch with user_id/category_id === undefined before
    // location.state populates them.
    (profileID && selectedCategoryID)
      ? [endpoints.getCategoryDetail, profileID, selectedCategoryID, start, limit, sortBy, sortOrder, isPlan14]
      : null,
    {
      fetcher: (url, user_id, category_id, startVal, limitVal, sort_by, sort_order, plan14Flag) => {
        const params = {
          user_id, category_id, start: startVal, limit: limitVal, vpm: false,
        };
        if (sortBy !== 'layout') {
          params.sort_by = sortBy;
          params.sort_order = sortOrder;
        } else if (plan14Flag) {
          params.sort_by = 'date';
          params.sort_order = 'ASC';
        }
        return dataFetcher(url, params);
      },
      onSuccess: (data, key) => {
        const { success, videos, message, video_count } = data;
        // Branch on the `start` that actually produced THIS response (the
        // SWR key it resolved for), not the closure's current `start` state —
        // those can differ when a slower earlier request resolves after a
        // later one, which was silently replacing/losing the appended page.
        const requestStart = Array.isArray(key) ? key[3] : start;
        if (success) {
          setIsSorting(false);
          if (videos.length === 0) {
            setHasMore(false);
          } else {
            let totalVideos;
            if (requestStart === 0) {
              setSubCategories([{ title: '', videos }]);
              totalVideos = videos.length;
            } else {
              setSubCategories((prev) => {
                const newVideos = [...(prev[0]?.videos || []), ...videos];
                totalVideos = newVideos.length;
                return [{ title: '', videos: newVideos }];
              });
            }
            setHasMore(totalVideos < video_count);
          }
        } else {
          setAppSnackbar({ isVisible: true, type: 'error', message: message || 'Oops! Something went wrong' });
        }
        setLoader(false);
        setIsLoadingMore(false);
        isLoadingMoreRef.current = false;
      },
      onError: (error) => {
        const responseData = error?.response?.data;
        logAppEvent({
          userName: `${userDetails?.firstname || localStorage.getItem('firstname') || ''} ${userDetails?.lastname || localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
          functionAffected: 'View Projector',
          description: `Inner page failed to load category videos — categoryID: ${selectedCategoryID}, profileID: ${profileID}, reason: ${responseData?.message || error?.message || 'Unknown error'}`,
          severity: 'high',
          errorMessage: error?.message || 'getCategoryDetail API failed',
          responseStatus: error?.response?.status || null,
          responseBody: responseData?.message || null,
          requestUrl: endpoints.getCategoryDetail,
          requestMethod: 'POST',
          component: 'InnerPage',
          userAction: 'View projector called',
        });
        setAppSnackbar({ isVisible: true, type: 'error', message: responseData?.message || 'Oops! Something went wrong' });
        setLoader(false);
        setIsLoadingMore(false);
        isLoadingMoreRef.current = false;
      },
    },
  );

  useEffect(() => {
    if (!location?.state) return;
    const incomingCategoryID = location.state.category_id ?? location.state.id;
    // Only the FIRST landing here was ever re-seeded (`!selectedCategoryID`) —
    // clicking "Show More" for a second, different category while this page
    // was still mounted from the previous one left `selectedCategoryID` (and
    // `start`) untouched, so the new category's first fetch quietly reused
    // the old category's scroll position (start=15/30 instead of 0).
    if (incomingCategoryID === selectedCategoryID) return;
    setSelectedCategoryID(incomingCategoryID);
    setProfileID(location.state.profile_id);
    setTitle(location.state.category_name ?? location.state.title);
    setThumbnail(location.state.icon);
    setSubCategories([]);
    setStart(0);
    setHasMore(true);
    setLoader(true);
  }, [location, selectedCategoryID]);

  // The actual fetch trigger — every dependency here mirrors the SWR key
  // above, so this fires exactly when a (re)fetch is actually needed: first
  // load, every load-more (`start` change), and every sort change. Calling
  // `mutate()` with NO arguments is SWR's documented way to force a
  // revalidate of the current key using the existing fetcher — unlike the
  // previous version of this call, which passed `(url, profileId, categoryId)`
  // as if they were fetcher args; `mutate`'s real signature is `(data, opts)`,
  // so that was silently corrupting the cache and firing a second, racing
  // request on top of this one.
  useEffect(() => {
    if (!profileID || !selectedCategoryID) return;
    getCategoryDetails();
  }, [profileID, selectedCategoryID, start, sortBy, sortOrder, isPlan14, getCategoryDetails]);

  useEffect(() => {
    const handleScroll = () => {
      // Ref check first: it updates synchronously, so a burst of native
      // scroll events in one tick can't all slip past the (delayed) state
      // read below and each queue a `setStart` — which was silently
      // skipping a whole page (start jumping 15 → 45 instead of → 30).
      // `loader` (the initial-load-in-flight flag) is checked too — without
      // it, a scroll event firing while the very first page (start=0) is
      // still loading could bump `start` to 15 before that first request had
      // even gone out, so the FIRST request ever sent was start=15, not 0.
      if (isLoadingMoreRef.current || !hasMore || loader) return;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const { scrollHeight, clientHeight } = document.documentElement;
      const scrollableHeight = scrollHeight - clientHeight;
      const scrollPercentage = (scrollTop / scrollableHeight) * 100;
      if (scrollPercentage >= 40) {
        isLoadingMoreRef.current = true;
        setIsLoadingMore(true);
        setStart((prev) => prev + limit);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, limit, loader]);

  const handleSort = (newSortBy, newSortOrder) => {
    if (sortBy === newSortBy && sortOrder === newSortOrder) return;
    setIsSorting(true);
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setSubCategories([]);
    setStart(0);
    setHasMore(true);
  };

  // Arm the "don't refetch /profile on back" flag as soon as we land here, so
  // it works regardless of which Back control is used (banner or the nav pill's
  // back arrow in the shared header).
  useEffect(() => {
    const pid = location?.state?.profile_id;
    if (pid != null) sessionStorage.setItem('skipProfileRefetch', String(pid));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const nextCategory = categories[catIndex + 1];
  const goToNextCategory = () => {
    if (!nextCategory) return;
    setSelectedCategoryID(nextCategory.category_id);
    setTitle(nextCategory.category_name);
    setThumbnail(nextCategory.icon);
    setCatIndex(catIndex + 1);
    setSubCategories([]);
    setStart(0);
    setHasMore(true);
    setLoader(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const videos = subCategories?.[0]?.videos || [];
  const hasVideos = videos.length > 0;
  const sortValue = `${sortBy}:${sortOrder}`;

  const ownerName = userDetails?.firstname?.substring(0, 10)
    || userDetails?.email?.split('@')[0]?.substring(0, 10);
  return (
    <Page noBottomBar noFixedSidebar noDefaultBg>
    <ViewerThemeProvider>
    <ViewerAurora color={projectorBg} />
    <Box sx={{ position: 'relative', zIndex: 1, minHeight: '100vh', color: '#fff' }}>
      {/* Full-bleed category banner — stretches behind the floating nav pill. */}
      <Box
        sx={{
          position: 'relative',
          width: '100vw',
          ml: 'calc(50% - 50vw)',
          mt: 'calc(-1 * var(--header-height, 64px))',
          height: { xs: 320, md: 460 },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: thumbnail ? `url(${thumbnail})` : 'linear-gradient(135deg, #1e2436, #0b0b0f)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: 'scale(1.02)',
          }}
        />
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, #000 2%, rgba(0,0,0,0.12) 55%, rgba(0,0,0,0.5) 100%)' }} />
        {/* Title + next-category on one line, aligned to the nav pill (86%). */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
          <Box sx={{
            width: '86%', mx: 'auto', px: { xs: '12px', md: '20px' }, pb: { xs: 2, md: 3 }, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2,
          }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: { xs: 24, md: 42 }, lineHeight: 1.1, textShadow: '0 2px 12px rgba(0,0,0,0.5)', minWidth: 0 }}>
              {title || 'Category'}
            </Typography>
            {nextCategory && (
              <Box
                role="button"
                tabIndex={0}
                onClick={goToNextCategory}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToNextCategory(); } }}
                sx={{ textAlign: 'right', cursor: 'pointer', flexShrink: 0, '&:hover .nextname': { color: '#5aa5ef' } }}
              >
                <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', lineHeight: 1.3 }}>
                  Next category
                </Typography>
                <Typography className="nextname" sx={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.9)', transition: 'color 0.15s ease' }}>
                  {nextCategory.category_name}
                  {' '}
                  →
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Content — aligned to the nav pill (86% centred). */}
      <Box sx={{ width: '86%', mx: 'auto', px: { xs: '12px', md: '20px' }, pt: 2, pb: 8 }}>
        {/* Count + sort */}
        {(hasVideos || isSorting) && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
              {`${videos.length} ${videos.length === 1 ? 'video' : 'videos'}`}
            </Typography>
            <Select
              value={sortValue}
              onChange={(e) => {
                const opt = SORT_OPTIONS.find((o) => o.value === e.target.value);
                if (opt) handleSort(opt.by, opt.order);
              }}
              size="small"
              sx={DARK_SELECT_SX}
              MenuProps={{ PaperProps: { sx: { bgcolor: '#17171c', color: '#fff', '& .MuiMenuItem-root:hover': { bgcolor: 'rgba(255,255,255,0.08)' } } } }}
            >
              {SORT_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {/* Negative gutter cancels the cards' hoverZoom padding so thumbnails
            line up flush with the count/sort row and the pill. */}
        <Box sx={{ mx: { xs: '-10px', md: '-15px' } }}>
          {loader ? (
            <GridShimmer />
          ) : !hasVideos ? (
            <Box sx={{ textAlign: 'center', py: 10 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 1 }}>No videos in this category yet.</Typography>
            </Box>
          ) : (
            <ViewerGrid
              videos={videos}
              isPreview
              onLockedClick={() => setShowPermissionModal(true)}
            />
          )}
        </Box>

        {isLoadingMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={30} />
          </Box>
        )}
      </Box>

      <PermissionDeniedModal
        open={showPermissionModal}
        onClose={() => setShowPermissionModal(false)}
      />
    </Box>
    </ViewerThemeProvider>
    </Page>
  );
};
